import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { generateScorecardPdf } from 'helpers/scorecardPdfGenerator'
import { isScorecardDomReady, waitForScorecardDomReady } from 'helpers/scorecardReady'
import CentaCandidateReport from './centaCandidateReport'
import './index.less'

const READY_POLL_MS = 500
const READY_TIMEOUT_MS = 120000
const PDF_TIMEOUT_MS = 120000

const readQueryParams = () => new URLSearchParams(window.location.search)

const ScorecardPdfDownload: React.FC = () => {
  const candidateId = useMemo(() => readQueryParams().get('candidateId') || '', [])
  const downloadStarted = useRef(false)
  const [status, setStatus] = useState('Preparing your scorecard PDF...')
  const [readyToDownload, setReadyToDownload] = useState(false)

  const generatePdf = useCallback(async () => {
    if (downloadStarted.current) {
      return
    }

    const scorecardEl = document.querySelector('.scorecard-document') as HTMLElement | null
    if (!scorecardEl) {
      setStatus('Scorecard not found. Please refresh and try again.')
      setReadyToDownload(true)
      return
    }

    downloadStarted.current = true
    setStatus('Generating PDF download...')

    try {
      await document.fonts.ready
      await Promise.race([
        generateScorecardPdf(scorecardEl, `CENTA_${candidateId}.pdf`),
        new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('PDF generation timed out')), PDF_TIMEOUT_MS)
        }),
      ])
      setStatus('Download started. You can close this tab.')
    } catch (error) {
      console.error('Scorecard PDF download failed', error)
      downloadStarted.current = false
      setStatus('PDF download failed. Use the buttons below to retry or print.')
      setReadyToDownload(true)
    }
  }, [candidateId])

  const printScorecard = useCallback(() => {
    window.print()
  }, [])

  useEffect(() => {
    document.documentElement.classList.add('scorecard-pdf-download')
    document.body.classList.add('scorecard-pdf-download')
    return () => {
      document.documentElement.classList.remove('scorecard-pdf-download')
      document.body.classList.remove('scorecard-pdf-download')
    }
  }, [])

  useEffect(() => {
    if (!candidateId) {
      return
    }

    downloadStarted.current = false
    setReadyToDownload(false)
    setStatus('Preparing your scorecard PDF...')

    const startedAt = Date.now()
    let intervalId = 0

    const pollReady = async () => {
      if (isScorecardDomReady()) {
        setReadyToDownload(true)
        setStatus('Scorecard ready. Starting download...')
        window.clearInterval(intervalId)
        await new Promise(resolve => window.setTimeout(resolve, 2000))
        generatePdf()
        return
      }

      if (Date.now() - startedAt > READY_TIMEOUT_MS) {
        const becameReady = await waitForScorecardDomReady(3000, 300)
        if (becameReady) {
          setReadyToDownload(true)
          setStatus('Scorecard ready. Starting download...')
          window.clearInterval(intervalId)
          generatePdf()
          return
        }

        setStatus('Unable to load scorecard. Open /candidatereport directly or use print.')
        setReadyToDownload(true)
        window.clearInterval(intervalId)
      }
    }

    intervalId = window.setInterval(pollReady, READY_POLL_MS)
    pollReady()

    return () => window.clearInterval(intervalId)
  }, [candidateId, generatePdf])

  if (!candidateId) {
    return (
      <div className="scorecard-pending">
        <p>Missing candidateId. Example: /download/scorecard-pdf?candidateId=TQ3001013&amp;testType=tq</p>
      </div>
    )
  }

  return (
    <div className="scorecard-pdf-download-page">
      <div className="scorecard-download-controls">
        <p>{status}</p>
        {readyToDownload && (
          <div className="scorecard-download-actions">
            <button type="button" className="scorecard-download-btn" onClick={generatePdf}>
              Download Scorecard PDF
            </button>
            <button type="button" className="scorecard-print-btn" onClick={printScorecard}>
              Print / Save as PDF
            </button>
          </div>
        )}
      </div>
      <CentaCandidateReport candidate={candidateId} />
    </div>
  )
}

export default ScorecardPdfDownload
